// to do: update and review articles
// Simple registry you can grow over time.
// NOTE: `sections[].body` expects HTML strings (same as your accordion content).

export const HELP_ARTICLES = {
    "getting-started": {
        slug: "getting-started",
        title: "Getting Started",
        category: "Learn",
        updatedAt: "Jan 2026",
        intro: "Mooz helps you practise interview answers the way you’ll deliver them: on camera, out loud, and under a little pressure. Set up your first session and build confidence from real playback and steady progress.",

        // primaryCta: { label: "Go to dashboard", href: "/dashboard" },
        // secondaryCta: { label: "Browse FAQs", href: "/faqs" },

        sections: [
            {
                id: "what-is-mooz",
                title: "What is Mooz?",
                body: `
                <p>
                    Mooz is a video interview practise space designed to feel realistic, but safe.
                    Instead of rehearsing in your head (or rewriting the same notes), you practise the
                    skill that interviews actually test: communicating clearly, confidently, and with structure.
                </p>
                <p>
                    You’ll work through real interview questions, draft quick talking points if you want,
                    then record answers you can replay and improve over time. Your recordings are tied to your account,
                    so you can track progress and revisit past attempts whenever you need to.
                </p>
            `,
                callout: {
                    title: "Best results come from consistency",
                    body: "Aim for short sessions a few times a week. Five focused recordings beat one long session every time.",
                },
            },
            {
                id: "sign-in",
                title: "Sign in and get set up",
                steps: [
                    {
                        title: "Create an account or sign in",
                        body: "Use Google sign-in or email authentication (via Auth0). It’s quick, secure, and keeps your recordings linked to you.",
                    },
                    {
                        title: "Arrive at your dashboard",
                        body: "Your dashboard is your starting point: jump into the question bank, run a mock interview, or check your recent activity.",
                    },
                    {
                        title: "Check your role and basics",
                        body: "Set the role you’re interviewing for (e.g. Software Engineer, Product, Data). Mooz uses this to keep your practise focused and relevant.",
                    },
                ],
            },
            {
                id: "how-to-practise",
                title: "Your first practise loop",
                body: `
                <p>
                    Mooz is built around a simple loop: <b>pick a question → record → replay → improve</b>.
                    The goal isn’t perfection - it’s clarity, structure, and confidence under time pressure.
                </p>
            `,
                steps: [
                    {
                        title: "Choose a question you can answer today",
                        body: "Start with something you’ve seen before - behavioural or role-specific. Early wins build momentum.",
                    },
                    {
                        title: "Draft quick talking points (optional)",
                        body: "Write a few bullet points to anchor your response. Keep it short: situation, your role, what you did, and the outcome.",
                    },
                    {
                        title: "Record in one take",
                        body: "Treat it like a real interview. No stopping, no re-doing mid-answer. You’ll learn faster from the honest playback.",
                    },
                    {
                        title: "Replay and choose one thing to improve",
                        body: "Pick one improvement per recording: structure, pacing, confidence, or specificity. Small upgrades compound quickly.",
                    },
                ],
            },
            {
                id: "question-bank",
                title: "Explore the question bank",
                body: `
                <p>
                    The question bank is where most of your progress will happen. You can browse by category,
                    open a question for guidance, and build a library of recordings you can compare over time.
                </p>
            `,
                bullets: [
                    "Browse questions by category or focus area.",
                    "Open a question to see tips and your saved recordings for that question.",
                    "Use drafts as lightweight prep - not a script.",
                ],
            },
            {
                id: "record",
                title: "Record your first answer",
                body: `
                <p>
                    Recording can feel awkward at first - that’s normal. Mooz is designed to make it easier:
                    a clear recording flow, a setup check, and a distraction-free experience once you press record.
                </p>
            `,
                steps: [
                    {
                        title: "Click Practice now",
                        body: "You’ll see a focused recording view so you can speak naturally without juggling tabs or notes.",
                    },
                    {
                        title: "Confirm camera and microphone",
                        body: "Choose the right devices, check your framing, and do a quick mic test before you start.",
                    },
                    {
                        title: "Save it to your account",
                        body: "Your answer is saved and attached to that question, so you can replay it later and measure improvement across attempts.",
                    },
                ],
                callout: {
                    title: "A great first recording is simple",
                    body: "Aim for 60–120 seconds, clear structure, and one measurable outcome. You can polish delivery later.",
                },
            },
            {
                id: "what-next",
                title: "What to do next",
                bullets: [
                    "Record 3 answers this week (short and focused).",
                    "Repeat 1 question and try a stronger structure the second time.",
                    "Run a 5-question mock when you’re ready to simulate the real thing.",
                ],
            },
        ],

        related: [
            {
                title: "How Mooz works",
                href: "/help/how-mooz-works",
                description: "Build confidence with repeatable practise loops.",
            },
            {
                title: "Camera or microphone issues",
                href: "/help/camera-mic-issues",
                description: "Fix permissions and device selection problems.",
            },
        ],
    },

    "structuring-answers-with-star": {
        slug: "structuring-answers-with-star",
        title: "Structuring Answers with STAR",
        category: "Learn",
        updatedAt: "Jan 2026",
        intro: "STAR keeps answers clear and concise: Situation, Task, Action, Result. It helps you show impact without rambling.",
        primaryCta: {
            label: "Try a STAR answer",
            href: "/dashboard/questions",
        },
        sections: [
            {
                id: "when-to-use-star",
                title: "When to use STAR",
                body: "<p>Use STAR for behavioral questions like “Tell me about a time...” or “Give me an example...”. It helps you stay structured even under pressure.</p>",
            },
            {
                id: "star-checklist",
                title: "STAR checklist",
                bullets: [
                    "Situation: Set the scene in one or two sentences.",
                    "Task: Name your responsibility or goal.",
                    "Action: Explain what you did and why.",
                    "Result: Share the outcome and impact.",
                ],
            },
            {
                id: "common-pitfalls",
                title: "Common pitfalls",
                bullets: [
                    "Too much context before the action.",
                    "Action steps are vague or generic.",
                    "No clear result or impact.",
                ],
            },
        ],
        related: [
            {
                title: "Getting started",
                href: "/help/getting-started",
                description: "Set up Mooz and record your first answer.",
            },
        ],
    },

    "turning-feedback-into-your-next-practice-plan": {
        slug: "turning-feedback-into-your-next-practice-plan",
        title: "Turning Feedback into Your Next Practice Plan",
        category: "Learn",
        updatedAt: "Jan 2026",
        intro: "Turn each review into a simple plan: pick one focus, set a target, and record again with that goal in mind.",
        primaryCta: { label: "Review a recording", href: "/dashboard" },
        sections: [
            {
                id: "pick-one-focus",
                title: "Pick one focus",
                body: "<p>Choose the single improvement that will help most right now, like structure, pacing, or specificity.</p>",
            },
            {
                id: "set-a-target",
                title: "Set a target",
                bullets: [
                    "Decide what “better” looks like in one sentence.",
                    "Keep the target measurable and short.",
                ],
            },
            {
                id: "record-again",
                title: "Record again",
                body: "<p>Do one new take with that target in mind. Keep the same question so you can compare clearly.</p>",
            },
            {
                id: "track-progress",
                title: "Track progress",
                body: "<p>Save your best take and note what changed. Small improvements add up fast.</p>",
            },
        ],
        related: [
            {
                title: "How Mooz works",
                href: "/help/how-mooz-works",
                description:
                    "Practice in short loops to build clarity and confidence.",
            },
        ],
    },

    "how-mooz-works": {
        slug: "how-mooz-works",
        title: "How Mooz Works",
        category: "Practice & Interviews",
        updatedAt: "Jan 2026",
        intro: "Mooz analyzes your transcript and provides feedback on your answer. It draws on common response patterns and recruiter guidance to personalise how you can improve before the real interview.",
        primaryCta: { label: "Start practising", href: "/dashboard/questions" },
        sections: [
            {
                id: "practise-loop",
                title: "The practise loop",
                steps: [
                    {
                        title: "Pick a question",
                        body: "Choose one that matches your target role or upcoming interview.",
                    },
                    {
                        title: "Draft key points",
                        body: "Optional - jot a short structure so you stay focused.",
                    },
                    {
                        title: "Record",
                        body: "Answer out loud in one uninterrupted take.",
                    },
                    {
                        title: "Review",
                        body: "Replay and pick one or two things to tighten next time.",
                    },
                ],
            },
            {
                id: "mock-interviews",
                title: "Mock interviews",
                body: "<p>Mocks group multiple questions to simulate pacing and pressure, helping you build stamina and consistency.</p>",
            },
        ],
        related: [
            {
                title: "Getting started",
                href: "/help/getting-started",
                description: "Set up Mooz and record your first answer.",
            },
        ],
    },

    "creating-an-account": {
        slug: "creating-an-account",
        title: "Creating an Account",
        category: "Account",
        updatedAt: "Jan 2026",
        intro: "Create a Mooz account securely using Google or email authentication via Auth0.",
        primaryCta: { label: "Sign up", href: "/signup" },
        sections: [
            {
                id: "sign-up",
                title: "Sign up",
                bullets: [
                    "Click Sign up from the homepage.",
                    "Choose Google sign-in or email sign-up.",
                    "You’ll be redirected back to Mooz automatically.",
                ],
            },
            {
                id: "security",
                title: "Security",
                body: "<p>Mooz uses Auth0 for authentication. We don’t store your password directly.</p>",
            },
        ],
        related: [
            {
                title: "Managing your profile",
                href: "/help/managing-your-profile",
                description: "Update your name and interview role.",
            },
        ],
    },

    "managing-your-profile": {
        slug: "managing-your-profile",
        title: "Managing Your Profile",
        category: "Account",
        updatedAt: "Jan 2026",
        intro: "Keep your profile up to date - including your interview role, which helps personalise your experience.",
        primaryCta: { label: "Open profile", href: "/profile" },
        sections: [
            {
                id: "edit-profile",
                title: "Edit your profile",
                bullets: [
                    "Go to Profile.",
                    "Update your name or interview role.",
                    "Save changes to update your account.",
                ],
            },
            {
                id: "role",
                title: "Interview role",
                body: "<p>Your interview role helps Mooz tailor prompts and insights. You can change it any time.</p>",
            },
        ],
        related: [
            {
                title: "Deleting your account",
                href: "/help/deleting-your-account",
                description: "Understand what happens if you delete your data.",
            },
        ],
    },

    "deleting-your-account": {
        slug: "deleting-your-account",
        title: "Deleting Your Account",
        category: "Account",
        updatedAt: "Jan 2026",
        intro: "Deleting your account permanently removes your profile and recordings. This action can’t be undone.",
        primaryCta: { label: "Go to profile", href: "/profile" },
        sections: [
            {
                id: "what-happens",
                title: "What happens when you delete your account",
                bullets: [
                    "Your profile is removed.",
                    "Your recordings are deleted.",
                    "Your practise history is removed.",
                ],
                callout: {
                    title: "Important",
                    body: "Account deletion is irreversible.",
                },
            },
            {
                id: "how-to",
                title: "How to delete",
                bullets: [
                    "Open Profile.",
                    "Go to the Danger Zone section.",
                    "Confirm deletion.",
                ],
            },
        ],
    },

    "camera-mic-issues": {
        slug: "camera-mic-issues",
        title: "Camera or Microphone Issues",
        category: "Technical",
        updatedAt: "Jan 2026",
        intro: "Most recording issues come from permissions, device selection, or another app using your camera/mic.",
        primaryCta: {
            label: "Try recording again",
            href: "/dashboard/questions",
        },
        sections: [
            {
                id: "permissions",
                title: "Check permissions",
                bullets: [
                    "In your browser, allow camera and microphone access for Mooz.",
                    "Refresh after granting permissions.",
                ],
            },
            {
                id: "devices",
                title: "Check device selection",
                bullets: [
                    "Select the correct camera and microphone in the setup preview.",
                    "Unplug and reconnect external microphones/headsets if needed.",
                ],
            },
            {
                id: "common-fixes",
                title: "Common fixes",
                bullets: [
                    "Close Zoom/Teams/Meet (they often lock devices).",
                    "Try Chrome if your current browser is blocking access.",
                    "Restart your browser (or your device) and try again.",
                ],
            },
        ],
        related: [
            {
                title: "Report a bug",
                href: "/help/report-a-bug",
                description: "Send details if the issue persists.",
            },
        ],
    },

    "report-a-bug": {
        slug: "report-a-bug",
        title: "Report a Bug",
        category: "Technical",
        updatedAt: "Jan 2026",
        intro: "Send us a clear bug report and we’ll fix issues faster.",
        sections: [
            {
                id: "what-to-include",
                title: "What to include",
                bullets: [
                    "What you were trying to do",
                    "What happened instead (error message if any)",
                    "Browser + version (e.g. Chrome 121)",
                    "Device (Mac/Windows/iOS/Android)",
                    "Screenshots or a short screen recording if possible",
                ],
            },
            {
                id: "send",
                title: "Where to send it",
                body: '<p>Email <a className="body-link" href="mailto:support@moozvideointerview.com">support@moozvideointerview.com</a>.</p>',
            },
        ],
    },
};
